import { useEffect, useState } from "react";
import type { BetaFeatureKey } from "@multi-chat/shared";
import { getOrganization } from "../../api/organizations-api.js";
import { useOrganizations } from "../organizations/organization-context.js";

type BetaFeaturesState =
  | { status: "idle" | "loading" }
  | { status: "ready"; enabled: ReadonlySet<BetaFeatureKey> }
  | { status: "error" };

/**
 * Reads enabledBetaFeatures from GET /organizations/:id without rewriting
 * organization-context.tsx. Refresh happens on organization switch / remount.
 */
export function useOrganizationBetaFeatures(): {
  loading: boolean;
  hasBetaFeature: (featureKey: BetaFeatureKey) => boolean;
} {
  const { selected } = useOrganizations();
  const organizationId = selected?.id ?? null;
  const [state, setState] = useState<BetaFeaturesState>({ status: "idle" });

  useEffect(() => {
    if (organizationId === null) {
      setState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    getOrganization(organizationId, controller.signal).then(
      (organization) => {
        if (controller.signal.aborted) return;
        setState({
          status: "ready",
          enabled: new Set(organization.enabledBetaFeatures),
        });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: "error" });
      },
    );
    return () => controller.abort();
  }, [organizationId]);

  return {
    loading: state.status === "loading" || state.status === "idle",
    hasBetaFeature: (featureKey) =>
      state.status === "ready" ? state.enabled.has(featureKey) : false,
  };
}
